/**
 * Recruiter Dashboard Page
 *
 * Main dashboard for recruiters showing stats, recent activity, and quick actions.
 */

import { Link } from 'react-router-dom';
import {
  useGetRecruiterDashboardStatsQuery,
  useListSavedCandidatesQuery,
  useListEvaluationSessionsQuery,
  useListSentContactRequestsQuery,
} from '../store/api/recruiterApi';

export function RecruiterDashboardPage() {
  const { data: stats, isLoading: statsLoading } = useGetRecruiterDashboardStatsQuery();
  const { data: savedCandidates } = useListSavedCandidatesQuery({ limit: 5 });
  const { data: evaluations } = useListEvaluationSessionsQuery({ status: 'active', limit: 5 });
  const { data: contacts } = useListSentContactRequestsQuery({ limit: 5 });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recruiter Dashboard</h1>
          <p className="mt-1 text-gray-500">
            Discover and connect with people who publish their practice
          </p>
        </div>
        <Link to="/marketplace" className="btn btn-primary">
          Find Candidates
        </Link>
      </div>

      {/* Stats Grid */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-gray-100 animate-pulse rounded-lg h-24" />
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Saved Candidates"
            value={stats.savedCandidatesCount}
            icon={<HeartIcon className="w-5 h-5" />}
            href="/recruiter/saved"
          />
          <StatCard
            label="Active Evaluations"
            value={stats.activeEvaluationsCount}
            icon={<ClipboardIcon className="w-5 h-5" />}
            href="/recruiter/evaluations"
          />
          <StatCard
            label="Pending Contacts"
            value={stats.pendingContactsCount}
            icon={<MailIcon className="w-5 h-5" />}
            color="yellow"
          />
          <StatCard
            label="Accepted Contacts"
            value={stats.acceptedContactsCount}
            icon={<CheckIcon className="w-5 h-5" />}
            color="green"
          />
          <StatCard
            label="Evaluations Made"
            value={stats.totalCreditsUsed}
            icon={<CoinIcon className="w-5 h-5" />}
            color="blue"
          />
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Recent Saved Candidates */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold">Recent Saved</h2>
            <Link to="/recruiter/saved" className="text-sm text-primary-600 hover:underline">
              View All
            </Link>
          </div>
          <div className="card-body">
            {savedCandidates?.items.length ? (
              <div className="space-y-3">
                {savedCandidates.items.map((candidate) => (
                  <div key={candidate.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-600">
                        {candidate.displayName?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {candidate.displayName || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {candidate.headline || 'No headline'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No saved candidates yet
              </p>
            )}
          </div>
        </div>

        {/* Active Evaluations */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold">Active Evaluations</h2>
            <Link to="/recruiter/evaluations" className="text-sm text-primary-600 hover:underline">
              View All
            </Link>
          </div>
          <div className="card-body">
            {evaluations?.items.length ? (
              <div className="space-y-3">
                {evaluations.items.map((session) => (
                  <Link
                    key={session.id}
                    to={`/recruiter/evaluations/${session.id}`}
                    className="block p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {session.title || 'Untitled Session'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {session.itemCount} candidates • {session.totalCreditsUsed} credits used
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 mb-3">No active evaluations</p>
                <Link to="/recruiter/evaluations/new" className="btn btn-sm btn-primary">
                  Start Evaluation
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Recent Contact Requests */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold">Recent Contacts</h2>
            <Link to="/recruiter/contacts" className="text-sm text-primary-600 hover:underline">
              View All
            </Link>
          </div>
          <div className="card-body">
            {contacts?.items.length ? (
              <div className="space-y-3">
                {contacts.items.map((contact) => (
                  <div key={contact.id} className="flex items-center gap-3">
                    <StatusBadge status={contact.status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {contact.roleTitle || 'No role specified'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Sent {new Date(contact.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No contact requests sent yet
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="card">
        <div className="card-body">
          <h2 className="font-semibold mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/marketplace"
              className="p-4 border rounded-lg text-center hover:bg-gray-50 transition-colors"
            >
              <SearchIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Find Talent</p>
              <p className="text-sm text-gray-500">Browse published profiles</p>
            </Link>
            <Link
              to="/recruiter/evaluations/new"
              className="p-4 border rounded-lg text-center hover:bg-gray-50 transition-colors"
            >
              <ClipboardIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">New Evaluation</p>
              <p className="text-sm text-gray-500">Listen and compare candidates</p>
            </Link>
            <Link
              to="/usage"
              className="p-4 border rounded-lg text-center hover:bg-gray-50 transition-colors"
            >
              <CoinIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Usage & Plan</p>
              <p className="text-sm text-gray-500">View your activity</p>
            </Link>
            <Link
              to="/profile"
              className="p-4 border rounded-lg text-center hover:bg-gray-50 transition-colors"
            >
              <UserIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Company Profile</p>
              <p className="text-sm text-gray-500">Manage your profile</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color?: 'gray' | 'green' | 'yellow' | 'blue';
  href?: string;
}

function StatCard({ label, value, icon, color = 'gray', href }: StatCardProps) {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    blue: 'bg-blue-50 text-blue-600',
  };

  const content = (
    <div className={`p-4 rounded-lg ${colorClasses[color]} ${href ? 'hover:opacity-80 transition-opacity' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );

  return href ? <Link to={href}>{content}</Link> : content;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || colors.pending}`}>
      {status}
    </span>
  );
}

// Icons
function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CoinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
